// TagsPanel.jsx — Admin-only tag management
// Tags control visibility: users sharing a tag can see each other online.
// Servers with a tag are only visible to users who also have that tag.
import { useState, useEffect, useCallback } from "react";
import { useTheme } from "../constants";
import { tags as tagsApi } from "../api";

const TAG_COLORS = [
  "#00D4AA","#3B82F6","#8B5CF6","#EC4899","#F59E0B",
  "#10B981","#EF4444","#6366F1","#C8922A","#14B8A6",
];

function Pill({ label, color, onRemove }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 20, background: `${color}22`, border: `1px solid ${color}55`, fontSize: 11, fontWeight: 700, color, marginRight: 4, marginBottom: 4 }}>
      {label}
      {onRemove && <button onClick={onRemove} style={{ background: "none", border: "none", cursor: "pointer", color, fontSize: 13, lineHeight: 1, padding: 0, marginLeft: 2 }}>×</button>}
    </span>
  );
}

function UserRow({ user, tagColor, tagged, onToggle }) {
  const C = useTheme();
  const hue = user.username ? user.username.charCodeAt(0) * 13 % 360 : 0;
  return (
    <div onClick={onToggle} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 10, cursor: "pointer", background: tagged ? `${tagColor}12` : "transparent", border: tagged ? `1px solid ${tagColor}44` : "1px solid transparent", marginBottom: 3, transition: "all 0.1s" }}>
      <div style={{ width: 30, height: 30, borderRadius: "50%", background: `hsl(${hue},50%,30%)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
        {user.username.slice(0, 2).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user.username}</div>
        {user.tag_names?.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 2, marginTop: 2 }}>
            {user.tag_names.map(t => <span key={t} style={{ fontSize: 10, color: C.muted, background: `${C.border}`, padding: "0 5px", borderRadius: 8 }}>{t}</span>)}
          </div>
        )}
      </div>
      <div style={{ width: 20, height: 20, borderRadius: "50%", border: `2px solid ${tagged ? tagColor : C.border}`, background: tagged ? tagColor : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.1s" }}>
        {tagged && <span style={{ color: "#0A0C10", fontSize: 11, fontWeight: 900 }}>✓</span>}
      </div>
    </div>
  );
}

function ServerRow({ server, tagColor, tagged, onToggle }) {
  const C = useTheme();
  return (
    <div onClick={onToggle} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 10, cursor: "pointer", background: tagged ? `${tagColor}12` : "transparent", border: tagged ? `1px solid ${tagColor}44` : "1px solid transparent", marginBottom: 3, transition: "all 0.1s" }}>
      <div style={{ width: 34, height: 34, borderRadius: 10, background: server.color || "#00D4AA", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>{server.icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{server.name}</div>
        {server.tag_names?.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 2, marginTop: 2 }}>
            {server.tag_names.map(t => <span key={t} style={{ fontSize: 10, color: C.muted, background: `${C.border}`, padding: "0 5px", borderRadius: 8 }}>{t}</span>)}
          </div>
        )}
      </div>
      <div style={{ width: 20, height: 20, borderRadius: "50%", border: `2px solid ${tagged ? tagColor : C.border}`, background: tagged ? tagColor : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.1s" }}>
        {tagged && <span style={{ color: "#0A0C10", fontSize: 11, fontWeight: 900 }}>✓</span>}
      </div>
    </div>
  );
}

export default function TagsPanel({ onClose }) {
  const C = useTheme();
  const [tagList, setTagList]   = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allServers, setAllServers] = useState([]);
  const [activeTag, setActiveTag] = useState(null); // tag id
  const [activeTab, setActiveTab] = useState("users"); // "users" | "servers"
  const [loading, setLoading]   = useState(true);
  const [newName, setNewName]   = useState("");
  const [newColor, setNewColor] = useState(TAG_COLORS[0]);
  const [creating, setCreating] = useState(false);
  const [search, setSearch]     = useState("");

  const load = useCallback(async () => {
    try {
      const [tl, au, as_] = await Promise.all([tagsApi.list(), tagsApi.allUsers(), tagsApi.allServers()]);
      setTagList(tl);
      setAllUsers(au);
      setAllServers(as_);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const currentTag = tagList.find(t => t.id === activeTag);

  async function createTag() {
    if (!newName.trim() || creating) return;
    setCreating(true);
    try {
      const t = await tagsApi.create(newName.trim(), newColor);
      setTagList(p => [...p, { ...t, users: [], servers: [] }]);
      setNewName("");
      setActiveTag(t.id);
    } catch (e) { alert(e.message); }
    setCreating(false);
  }

  async function deleteTag(id) {
    if (!window.confirm("Delete this tag? Users and servers will be untagged.")) return;
    try {
      await tagsApi.delete(id);
      setTagList(p => p.filter(t => t.id !== id));
      if (activeTag === id) setActiveTag(null);
      // Refresh all-users/servers to clear tag names
      const [au, as_] = await Promise.all([tagsApi.allUsers(), tagsApi.allServers()]);
      setAllUsers(au); setAllServers(as_);
    } catch {}
  }

  async function toggleUserTag(userId) {
    if (!currentTag) return;
    const isTagged = currentTag.users.some(u => u.id === userId);
    const newIds = isTagged
      ? currentTag.users.filter(u => u.id !== userId).map(u => u.id)
      : [...currentTag.users.map(u => u.id), userId];
    try {
      await tagsApi.setUsers(currentTag.id, newIds);
      // Update local tagList
      const userObjs = allUsers.filter(u => newIds.includes(u.id)).map(u => ({ id: u.id, username: u.username, avatar: u.avatar }));
      setTagList(p => p.map(t => t.id === currentTag.id ? { ...t, users: userObjs } : t));
      // Refresh all-users to update tag_names display
      tagsApi.allUsers().then(setAllUsers);
    } catch {}
  }

  async function toggleServerTag(serverId) {
    if (!currentTag) return;
    const isTagged = currentTag.servers.some(s => s.id === serverId);
    const newIds = isTagged
      ? currentTag.servers.filter(s => s.id !== serverId).map(s => s.id)
      : [...currentTag.servers.map(s => s.id), serverId];
    try {
      await tagsApi.setServers(currentTag.id, newIds);
      const serverObjs = allServers.filter(s => newIds.includes(s.id)).map(s => ({ id: s.id, name: s.name, icon: s.icon, color: s.color }));
      setTagList(p => p.map(t => t.id === currentTag.id ? { ...t, servers: serverObjs } : t));
      tagsApi.allServers().then(setAllServers);
    } catch {}
  }

  const filteredUsers   = allUsers.filter(u => u.username.toLowerCase().includes(search.toLowerCase()));
  const filteredServers = allServers.filter(s => s.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000090", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 16, width: "100%", maxWidth: 780, height: "min(90vh, 640px)", display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Header */}
        <div style={{ padding: "16px 20px 12px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.text, fontFamily: C.font }}>🏷️ Tags</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Assign tags to users and servers to control visibility. Users sharing a tag can see each other online. Servers with a tag are only visible to users with the same tag.</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 20, padding: 4, lineHeight: 1, flexShrink: 0 }}>✕</button>
        </div>

        <div style={{ flex: 1, display: "flex", minHeight: 0 }}>

          {/* Left: tag list */}
          <div style={{ width: 200, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", flexShrink: 0 }}>
            <div style={{ flex: 1, overflow: "auto", padding: "8px" }}>
              {loading && <div style={{ color: C.muted, fontSize: 12, textAlign: "center", padding: 20 }}>Loading…</div>}
              {!loading && tagList.length === 0 && (
                <div style={{ color: C.muted, fontSize: 12, textAlign: "center", padding: "20px 8px", lineHeight: 1.5 }}>
                  No tags yet.<br/>Create one to get started.
                </div>
              )}
              {tagList.map(t => (
                <button key={t.id} onClick={() => { setActiveTag(t.id); setSearch(""); }}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 9, border: activeTag === t.id ? `1px solid ${t.color}88` : "1px solid transparent", cursor: "pointer", background: activeTag === t.id ? `${t.color}18` : "transparent", textAlign: "left", marginBottom: 2 }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: t.color, flexShrink: 0 }}/>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.name}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>{t.users.length}u · {t.servers.length}s</div>
                  </div>
                </button>
              ))}
            </div>

            {/* New tag form */}
            <div style={{ padding: "8px 8px 12px", borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
              <input value={newName} onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && createTag()}
                placeholder="New tag name…" maxLength={24}
                style={{ width: "100%", background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, fontSize: 12, padding: "6px 9px", outline: "none", boxSizing: "border-box", marginBottom: 6 }}/>
              <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginBottom: 6 }}>
                {TAG_COLORS.map(col => (
                  <button key={col} onClick={() => setNewColor(col)}
                    style={{ width: 16, height: 16, borderRadius: "50%", border: newColor === col ? "2px solid #fff" : "2px solid transparent", background: col, cursor: "pointer", padding: 0 }}/>
                ))}
              </div>
              <button onClick={createTag} disabled={creating || !newName.trim()}
                style={{ width: "100%", background: newName.trim() ? C.accent : `${C.accent}44`, border: "none", borderRadius: 7, color: newName.trim() ? "#0A0C10" : C.muted, fontWeight: 700, fontSize: 12, padding: "6px 0", cursor: newName.trim() ? "pointer" : "default" }}>
                {creating ? "…" : "+ Create Tag"}
              </button>
            </div>
          </div>

          {/* Right: assignment panel */}
          {currentTag ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
              {/* Tag header */}
              <div style={{ padding: "10px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                <div style={{ width: 12, height: 12, borderRadius: "50%", background: currentTag.color }}/>
                <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{currentTag.name}</div>
                <div style={{ flex: 1 }}/>
                {/* Summary pills */}
                <span style={{ fontSize: 11, color: C.muted }}>{currentTag.users.length} user{currentTag.users.length !== 1 ? "s" : ""}</span>
                <span style={{ fontSize: 11, color: C.muted }}>·</span>
                <span style={{ fontSize: 11, color: C.muted }}>{currentTag.servers.length} server{currentTag.servers.length !== 1 ? "s" : ""}</span>
                <button onClick={() => deleteTag(currentTag.id)}
                  style={{ background: "#EF444422", border: "1px solid #EF444444", borderRadius: 7, color: "#EF4444", fontSize: 11, padding: "4px 9px", cursor: "pointer", marginLeft: 4 }}>Delete</button>
              </div>

              {/* Tabs */}
              <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
                {["users", "servers"].map(tab => (
                  <button key={tab} onClick={() => { setActiveTab(tab); setSearch(""); }}
                    style={{ flex: 1, padding: "9px 0", border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13, background: activeTab === tab ? `${currentTag.color}18` : "transparent", color: activeTab === tab ? currentTag.color : C.muted, borderBottom: activeTab === tab ? `2px solid ${currentTag.color}` : "2px solid transparent" }}>
                    {tab === "users" ? `👤 Users` : `🖥 Servers`}
                  </button>
                ))}
              </div>

              {/* Search */}
              <div style={{ padding: "8px 14px 4px", flexShrink: 0 }}>
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder={`Search ${activeTab}…`}
                  style={{ width: "100%", background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontSize: 13, padding: "6px 12px", outline: "none", boxSizing: "border-box" }}/>
              </div>

              {/* List */}
              <div style={{ flex: 1, overflow: "auto", padding: "4px 14px 14px" }}>
                {activeTab === "users" && (
                  filteredUsers.length === 0
                    ? <div style={{ color: C.muted, textAlign: "center", padding: 24, fontSize: 13 }}>No users found</div>
                    : filteredUsers.map(u => (
                        <UserRow key={u.id} user={u} tagColor={currentTag.color}
                          tagged={currentTag.users.some(tu => tu.id === u.id)}
                          onToggle={() => toggleUserTag(u.id)}/>
                      ))
                )}
                {activeTab === "servers" && (
                  filteredServers.length === 0
                    ? <div style={{ color: C.muted, textAlign: "center", padding: 24, fontSize: 13 }}>No servers found</div>
                    : filteredServers.map(s => (
                        <ServerRow key={s.id} server={s} tagColor={currentTag.color}
                          tagged={currentTag.servers.some(ts => ts.id === s.id)}
                          onToggle={() => toggleServerTag(s.id)}/>
                      ))
                )}
              </div>

              {/* Info footer */}
              <div style={{ padding: "8px 16px 10px", borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
                <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
                  <span style={{ color: currentTag.color, fontWeight: 700 }}>{currentTag.name}</span>
                  {" "}— users with this tag can see each other online. Servers with this tag are only accessible to tagged users. Untagged servers remain visible to all members.
                </div>
              </div>
            </div>
          ) : (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, color: C.muted, padding: 32 }}>
              <div style={{ fontSize: 36 }}>🏷️</div>
              <div style={{ fontSize: 14, textAlign: "center", fontWeight: 600, color: C.text }}>Select a tag to manage it</div>
              <div style={{ fontSize: 12, textAlign: "center", maxWidth: 300, lineHeight: 1.6 }}>
                Tags let you separate groups like Friends and Family. Users sharing a tag can see each other as online. Servers with a tag are hidden from users without it.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
